import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import ContactHelper from "../../../../entities/contact/contact-helper";
import Modal from "../../../../shared/components/dashboard-layout/components/modal/modalComponent";
import Input from "../../../../shared/components/dashboard-layout/components/inputFields/inputComponent";
import "./edit-contact-modal.scss";

const EditContactModal = ({ show, onHide, contact, onContactUpdated }) => {
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [platforms, setPlatforms] = useState([]);
  const [preferredPlatform, setPreferredPlatform] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show && contact) {
      setContactName(contact.name || "");
      setContactPhone(contact.phone && contact.phone !== "N/A" ? contact.phone : "");
      setContactEmail(contact.email && contact.email !== "N/A" ? contact.email : "");
      setError(null);
      (async () => {
        try {
          const details = await ContactHelper.getContactDetails(contact.id);
          const fetchedPlatforms = details?.platforms || [];
          setPlatforms(fetchedPlatforms);
          const defaultPlatform = fetchedPlatforms.find((p) => p.isDefault) || fetchedPlatforms[0] || null;
          setPreferredPlatform(defaultPlatform?.on || "");
        } catch (e) {
          setPlatforms([]);
          setPreferredPlatform("");
        }
      })();
    }
  }, [show, contact]);

  useEffect(() => {
    if (!show) {
      setContactName("");
      setContactPhone("");
      setContactEmail("");
      setPlatforms([]);
      setPreferredPlatform("");
      setError(null);
    }
  }, [show]);

  const handleClose = () => {
    setError(null);
    onHide();
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!contactName.trim()) {
      setError("Please enter a name.");
      return;
    }

    const hasPhone = Boolean(contactPhone.trim() && contactPhone.trim().toUpperCase() !== "N/A");
    const hasEmail = Boolean(contactEmail.trim() && contactEmail.trim().toUpperCase() !== "N/A");

    if (preferredPlatform === "whatsapp" && !hasPhone) {
      setError("WhatsApp selected as preferred but no phone number is provided.");
      return;
    }

    if (preferredPlatform === "email" && !hasEmail) {
      setError("Email selected as preferred but no email address is provided.");
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const updateData = { name: contactName.trim() };
      
      // Only include phone/email if they have values
      if (contactPhone.trim() && contactPhone.trim().toUpperCase() !== "N/A") {
        updateData.phone = contactPhone.trim();
      }
      if (contactEmail.trim() && contactEmail.trim().toUpperCase() !== "N/A") {
        updateData.email = contactEmail.trim();
      }
      // Build updated platforms
      let updatedPlatforms = Array.isArray(platforms) ? [...platforms] : [];

      const ensurePlatform = (on, referenceId) => {
        const idx = updatedPlatforms.findIndex((p) => p.on === on);
        if (idx === -1) {
          updatedPlatforms.push({ on, referenceId });
        } else if (referenceId) {
          updatedPlatforms[idx] = { ...updatedPlatforms[idx], referenceId };
        }
      };

      if (hasPhone) ensurePlatform("whatsapp", `whatsapp:${contactPhone.trim()}`);
      if (hasEmail) ensurePlatform("email", contactEmail.trim());

      if (updatedPlatforms.length > 0 && preferredPlatform) {
        updatedPlatforms = updatedPlatforms.map((p) => ({ ...p, isDefault: p.on === preferredPlatform }));
        // sanitize to only allowed fields
        updateData.platforms = updatedPlatforms.map((p) => {
          const sanitized = {
            on: p.on,
            referenceId: p.referenceId,
          };
          if (typeof p.isDefault === "boolean") sanitized.isDefault = p.isDefault;
          if (Array.isArray(p.unsubscribe)) sanitized.unsubscribe = p.unsubscribe;
          return sanitized;
        });
      }
      
      await ContactHelper.updateContact(contact.id, updateData);
      onContactUpdated();
      onHide();
    } catch (err) {
      // Handle specific duplicate contact error
      if (err?.response?.status === 409) {
        setError(
          err?.response?.data?.message ||
          "A contact with this email or phone number already exists in your organization."
        );
      } else {
        setError(
          err?.response?.data?.message ||
            "Failed to update contact. Please try again."
        );
      }
    }
    setIsLoading(false);
  };

  if (!show) return null;

  return (
    <Modal
      isVisible={show}
      onClose={() => {
        if (isLoading) return;
        handleClose();
      }}
      header={"Edit Contact"}
      className={"edit-contact-modal"}
      cancelButtonLabel={"Close"}
      cancelButtonAction={() => {
        if (isLoading) return;
        handleClose();
      }}
      positiveButtonLabel={isLoading ? "Saving..." : "Save Changes"}
      positiveButtonAction={() => {
        if (isLoading) return;
        handleSubmit();
      }}
    >
      {error && <div className="alert alert-danger mb-3">{error}</div>}

      <form onSubmit={handleSubmit} style={{ width: "100%" }}>
        <div className="form-group">
          <Input
            label={"Contact Name *"}
            placeholder={"Enter contact name"}
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            disabled={isLoading}
            required
          />
        </div>

        <div className="form-group">
          <Input
            type={"phone"}
            label={"Phone Number"}
            placeholder={
              contact?.phone && contact.phone !== "N/A"
                ? "Phone number cannot be edited"
                : "Enter phone number"
            }
            value={contactPhone}
            onChange={setContactPhone}
            disabled={isLoading || (contact?.phone && contact.phone !== "N/A")}
            phoneProps={{
              defaultCountry: "GB",
              international: true,
              withCountryCallingCode: true,
              countryCallingCodeEditable: false,
              countrySelectProps: { unicodeFlags: true },
            }}
          />
          <small className="text-muted">
            {contact?.phone && contact.phone !== "N/A"
              ? "Phone number cannot be edited"
              : "Add phone number (optional)"}
          </small>
        </div>

        <div className="form-group">
          <Input
            type={"email"}
            label={"Email Address"}
            placeholder={
              contact?.email && contact.email !== "N/A"
                ? "Email address cannot be edited"
                : "Enter email address"
            }
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            disabled={isLoading || (contact?.email && contact.email !== "N/A")}
          />
          <small className="text-muted">
            {contact?.email && contact.email !== "N/A"
              ? "Email address cannot be edited"
              : "Add email address (optional)"}
          </small>
        </div>

        <div className="form-group">
          <label>Preferred Platform</label>
          <div className="d-flex gap-3 align-items-center">
            <Input
              type={"radio"}
              id={"pref-whatsapp-edit"}
              name={"preferredPlatformEdit"}
              label={"WhatsApp"}
              value={"whatsapp"}
              checked={preferredPlatform === "whatsapp"}
              onChange={(e) => setPreferredPlatform(e.target.value)}
              disabled={isLoading}
            />
            <Input
              type={"radio"}
              id={"pref-email-edit"}
              name={"preferredPlatformEdit"}
              label={"Email"}
              value={"email"}
              checked={preferredPlatform === "email"}
              onChange={(e) => setPreferredPlatform(e.target.value)}
              disabled={isLoading}
            />
            {platforms.find((p) => p.on === "wechat") && (
              <Input
                type={"radio"}
                id={"pref-wechat-edit"}
                name={"preferredPlatformEdit"}
                label={"WeChat"}
                value={"wechat"}
                checked={preferredPlatform === "wechat"}
                onChange={(e) => setPreferredPlatform(e.target.value)}
                disabled={isLoading}
              />
            )}
          </div>
        </div>
      </form>
    </Modal>
  );
};

EditContactModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  contact: PropTypes.object,
  onContactUpdated: PropTypes.func.isRequired,
};

export default EditContactModal;